import React from 'react';
import {
  TextInput,
  required,
  useRecordContext,
  ReferenceInput,
  AutocompleteInput,
  email,
  regex,
} from 'react-admin';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  Box,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Description as NotesIcon,
  Badge as BadgeIcon,
  AccountTree as HierarchyIcon,
} from '@mui/icons-material';
import { CustomerEmailManager } from '../../components/CustomerEmailManager';

const phoneValidation = regex(/^[\d\s\-\+\(\)]+$/, 'Please enter a valid phone number');

interface CustomerFormProps {
  isEdit?: boolean;
}

export const CustomerForm: React.FC<CustomerFormProps> = ({ isEdit = false }) => {
  const record = useRecordContext();

  return (
    <Grid container spacing={3}>
      {/* Display Name - First and Separated */}
      <Grid item xs={12}>
        <Card sx={{ border: '2px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
          <CardContent>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: 'primary.main',
                fontWeight: 'bold',
              }}
            >
              <BadgeIcon color="primary" />
              Customer Identifier
            </Typography>
            <TextInput
              source="displayName"
              label="Display Name"
              validate={[required('Display name is required')]}
              fullWidth
              helperText="A unique name to identify this customer (e.g., 'John Smith - Main St' or 'Acme Corp')"
              sx={{ 
                '& .MuiInputBase-root': {
                  bgcolor: 'background.paper',
                }
              }}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Additional Details
          </Typography>
        </Divider>
      </Grid>

      {/* Company Information */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon color="primary" />
              Company Information
            </Typography>
            <TextInput
              source="company"
              label="Company Name (Optional)"
              fullWidth
              helperText="Enter company name if this is a business customer"
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Parent Customer - For hierarchical relationships */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HierarchyIcon color="primary" />
              Parent Organization
            </Typography>
            <ReferenceInput 
              source="parentCustomerId" 
              reference="customers"
              filter={{ id: { $ne: record?.id } }} // Exclude current customer from parent options
            >
              <AutocompleteInput
                label="Parent Customer (Optional)"
                optionText={(record) => 
                  record.displayName || 
                  `${record.firstName || ''} ${record.lastName || ''}`.trim() || 
                  record.company || 
                  'Unknown'
                }
                fullWidth
                helperText="Link to parent organization if this is a subsidiary or branch"
                allowEmpty
                emptyText="No parent customer"
              />
            </ReferenceInput>
          </CardContent>
        </Card>
      </Grid>

      {/* Point of Contact */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              Point of Contact
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextInput
                  source="firstName"
                  label="First Name"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput
                  source="lastName"
                  label="Last Name"
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextInput
              source="title"
              label="Title/Position"
              fullWidth
              sx={{ mt: 2 }}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Contact Information */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color="primary" />
              Contact Information
            </Typography>
            <TextInput
              source="phone"
              label="Primary Phone"
              fullWidth
              validate={[phoneValidation]}
              helperText="Include area code (e.g., +1 555-123-4567)"
              sx={{ mb: 2 }}
            />
            <TextInput
              source="email"
              label="Email Address"
              fullWidth
              validate={[email()]}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Location & Zone */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" />
              Location & Zone
            </Typography>
            <ReferenceInput source="zoneId" reference="crm/zones">
              <AutocompleteInput
                label="Service Zone"
                optionText="name"
                fullWidth
                sx={{ mb: 2 }}
              />
            </ReferenceInput>
            <ReferenceInput source="customerTypeId" reference="crm/customer-types">
              <AutocompleteInput
                label="Customer Type"
                optionText="name"
                fullWidth
              />
            </ReferenceInput>
          </CardContent>
        </Card>
      </Grid>

      {/* Additional Information */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotesIcon color="primary" />
              Additional Information
            </Typography>
            <TextInput
              source="notes"
              label="Notes"
              multiline
              rows={4}
              fullWidth
              helperText="Any additional information about this customer"
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Email Management - Only in Edit Mode */}
      {isEdit && record?.id && (
        <Grid item xs={12}>
          <CustomerEmailManager customerId={record.id} />
        </Grid>
      )}
    </Grid>
  );
};